<div class="isearch">
  <!-- ══════════════════════════════════════════════════════════════
       MOBILE: icon-only trigger button (hidden ≥ md)
  ══════════════════════════════════════════════════════════════════ -->
  <button
    class="isearch__mobile-trigger"
    type="button"
    aria-label="Abrir búsqueda"
    (click)="openMobileOverlay()"
  >
    <i class="pi pi-search" aria-hidden="true"></i>
  </button>

  <!-- ══════════════════════════════════════════════════════════════
       DESKTOP: inline combobox (hidden < md)
  ══════════════════════════════════════════════════════════════════ -->
  <div class="isearch__desktop">
    <div
      class="isearch__wrap"
      role="combobox"
      [attr.aria-expanded]="isOpen()"
      aria-haspopup="listbox"
      aria-owns="isearch-listbox"
    >
      <span class="isearch__icon" aria-hidden="true">
        <i class="pi pi-search"></i>
      </span>

      <input
        #inputRef
        class="isearch__input"
        type="search"
        autocomplete="off"
        spellcheck="false"
        [formControl]="searchControl"
        placeholder="Buscar módulos, rutas…"
        aria-label="Buscar rutas de la aplicación"
        aria-autocomplete="list"
        aria-controls="isearch-listbox"
        [attr.aria-activedescendant]="activeIndex() >= 0 ? 'isearch-option-' + activeIndex() : null"
        (keydown)="onKeydown($event)"
        (cancel)="clear()"
      />

      @if (searchControl.value) {
        <button
          class="isearch__clear"
          type="button"
          aria-label="Limpiar búsqueda"
          tabindex="-1"
          (click)="clear()"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      }
    </div>

    @if (isOpen()) {
      <ul
        id="isearch-listbox"
        class="isearch__dropdown"
        role="listbox"
        aria-label="Resultados de búsqueda"
      >
        <ng-container *ngTemplateOutlet="resultItems; context: { prefix: 'isearch-option' }" />
      </ul>
    }
  </div>

  <!-- ══════════════════════════════════════════════════════════════
       MOBILE OVERLAY (rendered only while open)
  ══════════════════════════════════════════════════════════════════ -->
  @if (isMobileOverlayOpen()) {
    <div
      class="isearch__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Buscador de rutas"
      (click)="closeMobileOverlay()"
    >
      <div class="isearch__overlay-panel" (click)="$event.stopPropagation()">
        <!-- Header: input + cancel -->
        <div class="isearch__overlay-header">
          <div
            class="isearch__wrap isearch__wrap--overlay"
            role="combobox"
            [attr.aria-expanded]="isOpen()"
            aria-haspopup="listbox"
            aria-owns="isearch-listbox-mobile"
          >
            <span class="isearch__icon" aria-hidden="true">
              <i class="pi pi-search"></i>
            </span>

            <input
              #mobileInputRef
              class="isearch__input"
              type="search"
              autocomplete="off"
              spellcheck="false"
              [formControl]="searchControl"
              placeholder="Buscar módulos, rutas…"
              aria-label="Buscar rutas de la aplicación"
              aria-autocomplete="list"
              aria-controls="isearch-listbox-mobile"
              [attr.aria-activedescendant]="
                activeIndex() >= 0 ? 'isearch-option-m-' + activeIndex() : null
              "
              (keydown)="onKeydown($event)"
            />

            @if (searchControl.value) {
              <button
                class="isearch__clear"
                type="button"
                aria-label="Limpiar búsqueda"
                tabindex="-1"
                (click)="clear()"
              >
                <i class="pi pi-times" aria-hidden="true"></i>
              </button>
            }
          </div>

          <button class="isearch__overlay-cancel" type="button" (click)="closeMobileOverlay()">
            Cancelar
          </button>
        </div>

        <!-- Results -->
        @if (isOpen()) {
          <ul
            id="isearch-listbox-mobile"
            class="isearch__dropdown isearch__dropdown--mobile"
            role="listbox"
            aria-label="Resultados de búsqueda"
          >
            <ng-container
              *ngTemplateOutlet="resultItems; context: { prefix: 'isearch-option-m' }"
            />
          </ul>
        }
      </div>
    </div>
  }
</div>

<!-- ── Shared results template ────────────────────────────────────────────── -->
<ng-template #resultItems let-prefix="prefix">
  @if (results().length === 0) {
    <li class="isearch__empty" role="status">
      <i class="pi pi-search" aria-hidden="true"></i>
      <span
        >Sin resultados para "<strong>{{ searchControl.value }}</strong
        >"</span
      >
    </li>
  } @else {
    @for (result of results(); track result.route; let i = $index) {
      <li
        [id]="prefix + '-' + i"
        class="isearch__option"
        [class.isearch__option--active]="activeIndex() === i"
        role="option"
        [attr.aria-selected]="activeIndex() === i"
        (click)="navigate(result)"
        (mouseenter)="activeIndex.set(i)"
      >
        <span class="isearch__option-icon" aria-hidden="true">
          <ng-container
            *ngTemplateOutlet="iconTpl; context: { $implicit: result.icon }"
          ></ng-container>
        </span>

        <span class="isearch__option-body">
          <span class="isearch__option-label">{{ result.label }}</span>
          @if (result.breadcrumb) {
            <span class="isearch__option-breadcrumb">{{ result.breadcrumb }}</span>
          }
        </span>
      </li>
    }
  }
</ng-template>

<!-- ── PrimeIcons icon template ────────────────────────────────── -->
<ng-template #iconTpl let-icon>
  @switch (icon) {
    @case ('home') {
      <i class="pi pi-home"></i>
    }
    @case ('lock') {
      <i class="pi pi-lock-open"></i>
    }
    @case ('chart-bar') {
      <i class="pi pi-chart-bar"></i>
    }
    @case ('bell') {
      <i class="pi pi-bell"></i>
    }
    @case ('dollar') {
      <i class="pi pi-dollar"></i>
    }
    @case ('wrench') {
      <i class="pi pi-wrench"></i>
    }
    @case ('certificate') {
      <i class="pi pi-verified"></i>
    }
    @case ('briefcase') {
      <i class="pi pi-briefcase"></i>
    }
    @case ('cloud') {
      <i class="pi pi-cloud"></i>
    }
    @case ('code') {
      <i class="pi pi-code"></i>
    }
    @case ('star') {
      <i class="pi pi-star"></i>
    }
    @default {
      <i class="pi pi-circle"></i>
    }
  }
</ng-template>
